import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { chartsAPI } from '../services/api';
import {
  Plus,
  BarChart3,
  TrendingUp,
  PieChart,
  AreaChart,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Copy,
  Calendar,
  Tag,
  Database,
  Sparkles,
  Target,
  Table,
  Activity,
  Zap,
  X,
  Loader2,
} from 'lucide-react';

interface Chart {
  id: string;
  name: string;
  type: string;
  indexName: string;
  description?: string;
  dataConfig?: {
    xAxis?: { field: string; label?: string };
    yAxis?: { field: string; label?: string; aggregation?: string };
    series?: Array<{ field: string; label?: string }>;
  };
  metadata?: {
    tags?: string[];
    isPublic?: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
}

const Charts: React.FC = () => {
  const [charts, setCharts] = useState<Chart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchCharts();
  }, []);

  const fetchCharts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await chartsAPI.getCharts(true);
      if (response.data.success) {
        setCharts(response.data.data || []);
      } else {
        setError(response.data.message || 'Error loading charts');
      }
    } catch (err: any) {
      console.error('Error fetching charts:', err);
      setError(err.response?.data?.message || 'Error loading charts');
    } finally {
      setLoading(false);
    }
  };

  const handleViewChart = (chartId: string) => {
    navigate(`/charts/${chartId}`);
  };

  const handleEditChart = (chartId: string) => {
    navigate(`/charts/${chartId}/edit`);
  };

  const handleDeleteChart = async (chartId: string) => {
    if (!window.confirm('Are you sure you want to delete this chart?')) {
      return;
    }

    try {
      await chartsAPI.deleteChart(chartId);
      await fetchCharts();
    } catch (err: any) {
      console.error('Error deleting chart:', err);
      alert('Error deleting chart');
    }
  };

  const handleDuplicateChart = async (chartId: string, chartName: string) => {
    try {
      await chartsAPI.duplicateChart(chartId, `${chartName} (Copie)`);
      await fetchCharts();
    } catch (err: any) {
      console.error('Error duplicating chart:', err);
      alert('Error duplicating chart');
    }
  };

  const getChartIcon = (type: string) => {
    const iconClass = "w-6 h-6";
    switch (type) {
      case 'bar':
        return <BarChart3 className={iconClass} />;
      case 'line':
        return <TrendingUp className={iconClass} />;
      case 'pie':
      case 'doughnut':
        return <PieChart className={iconClass} />;
      case 'area':
        return <AreaChart className={iconClass} />;
      case 'scatter':
        return <Target className={iconClass} />;
      case 'radar':
        return <Activity className={iconClass} />;
      case 'combo':
        return <Zap className={iconClass} />;
      case 'table':
        return <Table className={iconClass} />;
      default:
        return <BarChart3 className={iconClass} />;
    }
  };

  const getChartTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      bar: 'Barres',
      line: 'Lignes',
      pie: 'Secteurs',
      doughnut: 'Donut',
      area: 'Aires',
      scatter: 'Nuage',
      radar: 'Radar',
      combo: 'Combo',
      table: 'Tableau',
    };
    return labels[type] || type;
  };

  const getChartColor = (type: string) => {
    const colors: { [key: string]: string } = {
      bar: 'from-blue-500 to-blue-600',
      line: 'from-green-500 to-green-600',
      pie: 'from-purple-500 to-purple-600',
      doughnut: 'from-pink-500 to-pink-600',
      area: 'from-cyan-500 to-cyan-600',
      scatter: 'from-orange-500 to-orange-600',
      radar: 'from-indigo-500 to-indigo-600',
      combo: 'from-violet-500 to-violet-600',
      table: 'from-gray-500 to-gray-600',
    };
    return colors[type] || 'from-gray-500 to-gray-600';
  };

  const filteredCharts = charts.filter((chart) => {
    const matchesSearch =
      chart.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chart.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chart.indexName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chart.metadata?.tags?.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );
    const matchesType = selectedType === 'all' || chart.type === selectedType;
    return matchesSearch && matchesType;
  });

  const chartTypes = Array.from(new Set(charts.map((c) => c.type)));

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
              <p className="text-gray-300">Loading your charts...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-6 backdrop-blur-md">
            <p className="text-red-300 mb-4">{error}</p>
            <button
              onClick={fetchCharts}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
              Mes Graphiques
            </h1>
            <p className="text-gray-300">
              Manage and view your saved charts ({charts.length} total)
            </p>
          </div>
          <div className="flex gap-3">
          <button
              onClick={() => navigate('/search')}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-all backdrop-blur-md"
          >
            <Plus className="w-5 h-5" />
              Create a chart
          </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-lg">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher par nom, description, index ou tags..."
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-white placeholder-gray-400"
              />
            </div>
            {/* Type Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-white appearance-none"
                style={{ colorScheme: 'light' }}
              >
                <option value="all" className="bg-gray-800">All types</option>
                {chartTypes.map((type) => (
                  <option key={type} value={type} className="bg-gray-800">
                    {getChartTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        {filteredCharts.length === 0 ? (
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-12 text-center shadow-lg">
            <BarChart3 className="w-20 h-20 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-white mb-2">
              {charts.length === 0 ? 'No charts created' : 'No results'}
            </h3>
            <p className="text-gray-300 mb-6">
              {charts.length === 0
                ? 'Create your first chart to start visualizing your data'
                : 'Try adjusting your search criteria'}
            </p>
            <button
              onClick={() => navigate('/search')}
              className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-violet-500 text-white rounded-lg hover:from-cyan-600 hover:to-violet-600 transition-all shadow-lg hover:shadow-cyan-500/50"
            >
              {charts.length === 0 ? 'Create my first chart' : 'Create a chart'}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCharts.map((chart) => (
              <div
                key={chart.id}
                className="group bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:border-cyan-400/50 transition-all shadow-lg hover:shadow-cyan-500/20"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`p-3 bg-gradient-to-br ${getChartColor(chart.type)} rounded-xl text-white shadow-lg`}>
                      {getChartIcon(chart.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white truncate" title={chart.name}>
                        {chart.name}
                      </h3>
                      <span className="inline-block mt-1 px-2 py-1 text-xs bg-cyan-500/20 text-cyan-300 rounded-full border border-cyan-400/30">
                        {getChartTypeLabel(chart.type)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {chart.description && (
                  <p className="text-sm text-gray-300 mb-4 line-clamp-2">
                    {chart.description}
                  </p>
                )}

                {/* Configuration Info */}
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Database className="w-4 h-4" />
                    <span className="truncate">{chart.indexName}</span>
                  </div>
                  {chart.dataConfig?.xAxis && chart.dataConfig?.yAxis && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <BarChart3 className="w-4 h-4" />
                      <span>
                        {chart.dataConfig.xAxis.label || chart.dataConfig.xAxis.field} ×{' '}
                        {chart.dataConfig.yAxis.label || chart.dataConfig.yAxis.field}
                      </span>
                    </div>
                  )}
                  {chart.metadata?.createdAt && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {new Date(chart.metadata.createdAt).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {chart.metadata?.tags && chart.metadata.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {chart.metadata.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gradient-to-r from-cyan-500/20 to-violet-500/20 border border-cyan-400/30 text-cyan-300 rounded-full"
                      >
                        <Tag className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                    {chart.metadata.tags.length > 3 && (
                      <span className="px-2 py-1 text-xs text-gray-400">
                        +{chart.metadata.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-white/10">
                  <button
                    onClick={() => handleViewChart(chart.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-violet-500 text-white rounded-lg hover:from-cyan-600 hover:to-violet-600 transition-all text-sm font-medium"
                  >
                    <Eye className="w-4 h-4" />
                    Voir
                  </button>
                  <button
                    onClick={() => handleEditChart(chart.id)}
                    className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-all"
                    title="Modifier"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDuplicateChart(chart.id, chart.name)}
                    className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-all"
                    title="Dupliquer"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteChart(chart.id)}
                    className="px-4 py-2 bg-red-500/20 border border-red-400/30 text-red-300 rounded-lg hover:bg-red-500/30 transition-all"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Charts;
